import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ru';
import 'dayjs/locale/az';
import { i18n as i18nType } from 'i18next';
import { getLangDefinition } from './language';
import CONFIG from '../../config';
import {
  DEFAULT_CATEGORY_VALUES,
  TIME_FORMAT,
  PHONE_INTERNATIONAL_CODE,
  BUILDING_TYPE_ID
} from './constants';
import { Coordinates } from '../../providers/GoogleMapsProvider/types';
import { Category } from '../../graphql/graphql';
import type { PropertyValue, TFunction, ValidatorsConfigEntry } from './types';

export type { PropertyValue, TFunction, ValidatorsConfigEntry };

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Normalizes a string for case-insensitive searches:
 * 1. Applies Unicode NFD normalization to decompose characters (e.g. "i̇" → ["i", combining dot])
 * 2. Strips out all combining diacritical marks (accents, dots, etc.)
 * 3. Converts the result to lowercase using the default toLowerCase()
 *
 * @param str - The input string to normalize
 * @returns The normalized, lowercase string without diacritics
 */
export const normalizeForSearch = (str: string) =>
  str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const formatNumber = (value: string | undefined) => {
  if (typeof value === 'undefined') return value;
  const onlyNums = value.replace(/[^\d]/g, '').replace(/^0+/, '0');
  return onlyNums || undefined;
};

export const entitiesFilter = <T extends { name?: string | null }>(
  array: T[] = [],
  searchString = ''
) => {
  const normalizedSearchString = normalizeForSearch(searchString);

  if (!normalizedSearchString) return array;

  const filtered = array.filter(item => {
    const normalizedItemName = normalizeForSearch(String(item.name));
    return normalizedItemName.includes(normalizedSearchString);
  });

  return filtered.sort((a, b) => {
    const nameA = normalizeForSearch(String(a.name));
    const nameB = normalizeForSearch(String(b.name));
    const aStarts = nameA.startsWith(normalizedSearchString);
    const bStarts = nameB.startsWith(normalizedSearchString);

    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return 0;
  });
};

export const isIOS = () => {
  if (typeof navigator !== 'undefined') {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }
  return false;
};

export const isAndroid = () => {
  if (typeof navigator !== 'undefined') {
    return /android/i.test(navigator.userAgent);
  }
  return false;
};

export const getGoogleApiKey = () => {
  const isRunAtLocalhost =
    typeof window !== 'undefined'
      ? window.location.origin.match(/(lvh\.me|localhost)/g)
      : false;
  return isRunAtLocalhost ? CONFIG.googleApiKeyLocalhost : CONFIG.googleApiKey;
};

export const getObjCopy = (obj: object): object =>
  obj ? JSON.parse(JSON.stringify(obj)) : {};

export const formatNumberWithComma = (number?: number | string | null) => {
  if (typeof number === 'undefined') return number;
  return number?.toLocaleString('ru', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const flatIds = ['1', '2', '3'];

export const checkIsFlatCategory = (categoryId?: string) =>
  flatIds.includes(categoryId || '');

export const isExistAndPositiveInteger = (value?: string | number | null) => {
  const num = typeof value === 'string' ? Number(value) : value;
  return num && num > 0;
};

export const accusative = (word: string, lng: string) => {
  if (lng !== 'ru') {
    return word;
  }
  return word.replace(/а$/, 'у');
};

export const transformSlug = (slug?: string | null) => {
  if (!slug) {
    return;
  }
  const slugArr = slug.split('/');
  slugArr.shift();
  return slugArr.join('/');
};

export const convertToNumber = (value?: string | number) => {
  if ((typeof value === 'string' && !value.length) || value === undefined) {
    return;
  }
  return +value;
};

export const convertToBoolean = (value?: boolean) => {
  if (typeof value !== 'boolean' || !value) {
    return;
  }
  return value;
};

export const getValueFromObject = <T = string>(
  initialArray: Record<string, PropertyValue>[] = [],
  property: string | number | null | undefined,
  returnProperty: string,
  searchKey?: string
): T | undefined =>
  initialArray.reduce<T | undefined>((acc, item) => {
    if (acc !== undefined) return acc;

    const isMatch = searchKey
      ? item[searchKey] === property
      : Object.values(item).some(value => value === property);

    if (isMatch) {
      return item[returnProperty] as T;
    }

    if (Array.isArray(item.children)) {
      return getValueFromObject<T>(
        item.children as Record<string, PropertyValue>[],
        property,
        returnProperty,
        searchKey
      );
    }

    return acc;
  }, undefined);

const checkTodayOrYesterday = (
  today: dayjs.Dayjs,
  date: dayjs.Dayjs,
  i18n: i18nType
) =>
  date.isBefore(today) ? i18n.t('common.yesterday') : i18n.t('common.today');

export const formatDate = (
  d: dayjs.ConfigType,
  i18n: i18nType,
  template = TIME_FORMAT.fullDateTime
) => {
  const localeDate = dayjs(d).locale(getLangDefinition(i18n.language));

  return localeDate.format(template);
};

export const formatToBakuTime = (
  isoDateStr: dayjs.ConfigType,
  i18n: i18nType,
  template: string
) => {
  const bakuTime = dayjs(isoDateStr).utc().tz('Asia/Baku');
  return formatDate(bakuTime, i18n, template);
};

export const dateToFormat = (
  date: dayjs.ConfigType,
  format = 'DD MMMM YYYY, HH:mm'
) => dayjs(date).format(format);

export const getCardTime = (
  i: dayjs.ConfigType,
  i18n: i18nType,
  timeFormat?: string
) => {
  const todayMidnight = dayjs().tz('Asia/Baku').startOf('day');
  const localeDate = dayjs(i)
    .tz('Asia/Baku')
    .locale(getLangDefinition(i18n.language));
  const { fullDateOnly, timeOnly } = TIME_FORMAT;

  if (localeDate.isBefore(todayMidnight.subtract(1, 'day'))) {
    return formatToBakuTime(i, i18n, timeFormat || fullDateOnly);
  }

  return `${checkTodayOrYesterday(
    todayMidnight,
    localeDate,
    i18n
  )} ${formatToBakuTime(i, i18n, timeOnly)}`;
};

export const toQueryString = (
  obj: Record<string, PropertyValue>,
  prefix?: string
): string => {
  const str: string[] = [];
  let newPrefix: string;
  let property: PropertyValue;

  Object.keys(obj || {}).forEach(item => {
    newPrefix = prefix ? `${prefix}[]` : item.replace(/[[\]]/g, '');
    property = obj[item];

    str.push(
      typeof property === 'object'
        ? toQueryString(property as Record<string, PropertyValue>, newPrefix)
        : `${encodeURI(newPrefix)}=${encodeURIComponent(property as string)}`
    );
  });

  return str.join('&');
};

export const isNotNil = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

export const prettyNumber = (number?: number | string | null) =>
  number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

export const getCallCenterPhones = () => {
  const {
    contacts: {
      tel,
      tel_url: telUrl,
      tel_additional: telAdditional,
      tel_additional_url: telAdditionalUrl
    }
  } = CONFIG;

  const phonesArr = [
    { tel, telUrl },
    { tel: telAdditional, telUrl: telAdditionalUrl }
  ];

  return phonesArr.filter(
    (phone, index) =>
      phonesArr.findIndex(item => item.tel === phone.tel) === index
  );
};

export const getFirstPaymentAmount = (price: number, percent: number) =>
  Math.floor((price / 100) * percent);

export const setCoordinates = (
  data: { latitude?: number | null; longitude?: number | null } | undefined,
  change: (coordinates: Coordinates) => void
) => {
  const coordinates = {
    lat: data?.latitude,
    lng: data?.longitude
  };
  change(coordinates);
};

export const scrollInputToCenter = (input?: HTMLElement | null) => {
  const safeOffset = 60;
  if (isIOS()) {
    setTimeout(() => {
      if (input) {
        const rect = input.getBoundingClientRect();
        const inputBottom = rect.bottom + safeOffset;

        if (inputBottom > window.innerHeight) {
          input.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  }
};

export const isLeasedCategory = (id: string) => ['lease', 'sale'].includes(id);

export const snakeToCamelCase = (str: string) =>
  str
    .toLowerCase()
    .replace(/([-_][a-z])/g, group =>
      group.toUpperCase().replace('-', '').replace('_', '')
    );

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

export const animateHorizontalScroll = (
  element: HTMLElement,
  target: number,
  duration: number
) => {
  const start = element.scrollLeft;
  const change = target - start;
  let startTime: number | null = null;

  const animate = (timestamp: number) => {
    if (!startTime) startTime = timestamp;
    const timeElapsed = timestamp - startTime;
    const progress = Math.min(timeElapsed / duration, 1);

    element.scrollLeft = start + change * progress;

    if (timeElapsed < duration) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

export const capitalizeFirstLetter = (str?: string | null) =>
  str ? `${str.charAt(0).toUpperCase()}${str.slice(1)}` : '';

export const findCategory = ({
  id,
  categories
}: {
  id: string;
  categories: Category[];
}): Category | undefined =>
  categories.reduce<Category | undefined>((acc, cat) => {
    if (acc) return acc;
    if (cat.id === id) return cat;
    if (cat.children && cat.children.length) {
      const childResult = findCategory({ id, categories: cat.children });
      if (childResult) return childResult;
    }
    return acc;
  }, undefined);

export const getCategoryValues = ({
  id,
  categories
}: {
  id: string | null | undefined;
  categories: Category[] | null | undefined;
}) => {
  if (!id || !categories?.length) {
    return DEFAULT_CATEGORY_VALUES;
  }
  const {
    hasRooms,
    hasFloor,
    hasRepair,
    areaUnits,
    hasLandArea,
    photosRequired,
    hasBuildingType,
    areRoomsRequired
  } = findCategory({
    id,
    categories
  })!;

  return {
    hasRooms,
    hasFloor,
    hasRepair,
    areaUnits,
    hasLandArea,
    photosRequired,
    hasBuildingType,
    areRoomsRequired
  };
};

export const formatDateNumbers = (i: dayjs.ConfigType, i18n: i18nType) => {
  const localeDate = dayjs(i).locale(getLangDefinition(i18n.language));
  return localeDate.format('DD.MM.YYYY, HH:mm');
};

export const normalizePhoneNumber = (phoneNumber?: string | number | null) => {
  const cleaned = `${phoneNumber}`.replace(/\D/g, '').split('');

  const joinSlice = (array: string[], start: number, end: number) =>
    array.slice(start, end).join('');

  return `${joinSlice(cleaned, 0, 3)} ${joinSlice(cleaned, 3, 6)} ${joinSlice(
    cleaned,
    6,
    8
  )} ${joinSlice(cleaned, 8, 10)}`;
};

export const phoneWithInternationalCode = (phone?: string | null) => {
  if (!phone) return;

  return phone.startsWith('(0')
    ? PHONE_INTERNATIONAL_CODE + phone.replace(/\D/g, '').replace(/^0/, '')
    : phone;
};

export const formatNumberWithTwoDecimals = (number?: number | null) => {
  if (number) {
    return prettyNumber(number.toFixed(2).replace('.', ','));
  }
};

export const formatNumberWithSpaces = (number?: number | null) => {
  if (number === null || number === undefined) return;

  const isInteger = Number.isInteger(number);
  const formatted = isInteger
    ? prettyNumber(number)
    : formatNumberWithTwoDecimals(number);

  return formatted;
};

export const validateFunc =
  <TValues extends Record<string, PropertyValue>>(
    t: (key: string) => string,
    validatorsConfig: Record<string, ValidatorsConfigEntry<TValues>>
  ) =>
  (values: TValues): Record<string, string> =>
    Object.entries(validatorsConfig).reduce(
      (errors, [fieldName, config]) => {
        const { required, validator, errorMessage } = config;
        const isRequired =
          typeof required === 'function' ? required(values) : required;
        const fieldValue = values[fieldName] as TValues[keyof TValues & string];
        const condition =
          !fieldValue || (validator && !validator(fieldValue, values));
        if (isRequired && condition) {
          const error =
            typeof errorMessage === 'function'
              ? errorMessage(values, t)
              : t(errorMessage);
          return { ...errors, ...(error && { [fieldName]: error }) };
        }
        return errors;
      },
      {} as Record<string, string>
    );

export const filterByKey = <
  T extends Record<string, PropertyValue>,
  U extends Record<string, PropertyValue>
>(
  items: T[] | null | undefined,
  targetObject: U | null | undefined,
  itemKeyPath: string | null | undefined,
  targetKeyPath: string | null | undefined
): T | null => {
  if (!items || !targetObject || !itemKeyPath || !targetKeyPath) return null;

  const targetValue = targetKeyPath
    .split('.')
    .reduce<PropertyValue>(
      (acc, key) => (acc as Record<string, PropertyValue>)?.[key],
      targetObject
    );

  return (
    items.find(item => {
      const itemValue = itemKeyPath
        .split('.')
        .reduce<PropertyValue>(
          (acc, key) => (acc as Record<string, PropertyValue>)?.[key],
          item
        );
      return itemValue === targetValue;
    }) || null
  );
};

export const formatAzerbaijanPhone = (phone?: string) => {
  const cleaned = (phone || '').replace(/\D/g, '');
  if (!cleaned.startsWith('994') || cleaned.length < 12) return phone;

  const country = cleaned.slice(0, 3);
  const operator = cleaned.slice(3, 5);
  const part1 = cleaned.slice(5, 8);
  const part2 = cleaned.slice(8, 10);
  const part3 = cleaned.slice(10, 12);

  return `+${country} ${operator} ${part1} ${part2} ${part3}`;
};

export const removeUndefinedFields = <T extends object>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined)
  ) as Partial<T>;

export const getBasedDataStatValueInFilters = (isPasha = false) =>
  isPasha ? 'pasha-landing' : 'new-search';

export const checkIsAreaCategory = (categoryId?: string) =>
  BUILDING_TYPE_ID.AREA === categoryId;

export const checkIsCategorySupportsPriceToggle = (
  state: { category_id?: string; leased?: string } | null | undefined
) => {
  if (!state?.category_id) return false;

  switch (state.category_id) {
    case BUILDING_TYPE_ID.AREA:
      return state.leased !== 'true';

    default:
      return false;
  }
};

export const roundNumber = (value: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;

  const rounded = Math.round(Math.max(value, 1));

  return prettyNumber(rounded);
};
